import {
    Button,
    FormControl,
    Heading,
    List,
    ListItem,
    Text,
    VStack,
} from '@chakra-ui/react';
import startCase from 'lodash/startCase';
import PropTypes from 'prop-types';
import QRCode from 'qrcode.react';
import React, { useEffect, useState } from 'react';
import { Trans, withTranslation } from 'react-i18next';
import { verifyOtp } from '../../api/auth';
import { buildUrlViaQueryString } from '../../api/newUtils';
import { useCurrentColors } from '../../hooks/UseCurrentColors/UseCurrentColors';
import { useCheckForMobile } from '../../hooks/UseResponsive/UseResponsive';
import CanopyButton from '../CanopyButton/CanopyButton';
import CanopyInput from '../CanopyInput/CanopyInput';
import ErrorBox from '../ErrorBox/ErrorBox';
import { LoginFlow, LoginPageProps } from './LoginPage';

interface LoginFlowProps extends LoginPageProps {
    setLoginFlow: React.Dispatch<React.SetStateAction<LoginFlow>>;
    t: any;
    user: any;
    setLoginResponse: React.Dispatch<
        React.SetStateAction<Record<string, unknown>>
    >;
}
const Verification: React.FC<LoginFlowProps> = (props) => {
    const { setLoginFlow, api, t, user, setLoginResponse } = props;
    const currentColors = useCurrentColors();
    const isMobile = useCheckForMobile();
    const [verificationCode, setVerificationCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [ttOtpUri, setTtOtpUri] = useState<string>('');
    const getTtotpUri = () => {
        let issuer = process.env.REACT_APP_APPNAME;
        if (process.env.NODE_ENV !== 'production') {
            issuer += ' - ' + startCase(process.env.NODE_ENV || 'development');
        }
        return buildUrlViaQueryString(
            `otpauth://totp/${user.username || 'user'}`,
            {
                secret: user.secret_key,
                issuer,
            }
        );
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);
        try {
            await verifyOtp({
                code: verificationCode.toString(),
            });
            setLoginFlow('login');
            setIsLoading(false);
        } catch (e: any) {
            if (e?.response?.body) {
                const { body } = e.response;
                setError(body?.error ? body?.error : 'Something went wrong');
            } else if (e?.response?.data) {
                const { data } = e.response;
                if (data.login_flow === 'reset_password') {
                    setLoginResponse({ ...data });
                    setLoginFlow('reset_password');
                } else {
                    setError(
                        data?.error ? data?.error : 'Something went wrong'
                    );
                }
            } else {
                setError('Something went wrong');
            }
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (user) {
            setTtOtpUri(getTtotpUri());
        }
    }, [user]);

    return (
        <>
            <Heading
                as="h5"
                size="sm"
                mb="20px"
                mt="20px"
                textAlign="left">
                {t('two-factor.set-up-authenticator')}
            </Heading>
            <List
                as="ol"
                styleType="decimal"
                mt="1em"
                mb="1em"
                fontSize="13px"
                spacing="1">
                <ListItem>
                    <Trans i18nKey="two-factor.add-code-to-authenticator">
                        <strong>Download Authenticator app</strong>. Links
                        below:
                    </Trans>
                    <List
                        styleType="disc"
                        pl="20px">
                        <ListItem>
                            <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2">
                                <u>{t('two-factor.download-for-android')}</u>
                            </a>
                        </ListItem>
                        <ListItem>
                            <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2">
                                <u>{t('two-factor.download-for-android')}</u>
                            </a>
                        </ListItem>
                        <ListItem>
                            <a href="https://www.microsoft.com/en-in/store/p/authenticator/9nblggh08h54\">
                                <u>{t('two-factor.download-for-windows')}</u>
                            </a>
                        </ListItem>
                    </List>
                </ListItem>
                <ListItem>
                    <Trans i18nKey="two-factor.select-setup-account">
                        In the Authenticator app, click <strong>+</strong> to
                        add a new account
                    </Trans>
                </ListItem>
                <ListItem>
                    <Trans i18nKey="two-factor.scan-or-enter">
                        <strong>Scan QR code</strong> or enter the code manually
                    </Trans>
                </ListItem>
            </List>
            <QRCode
                value={ttOtpUri}
                size={150}
            />
            <br />
            <Text
                fontSize="12px"
                fontWeight="700">
                {t('two-factor.code')}:
            </Text>
            <code
                style={{
                    fontFamily: 'monospace, monospace !important',
                    fontSize: '12px',
                    width: '100%',
                }}>
                {user.secret_key}
            </code>
            <br />
            <form
                onSubmit={handleSubmit}
                style={{
                    width: isMobile ? '342px' : 'initial',
                    marginLeft: isMobile ? '7px' : 'initial',
                    marginRight: isMobile ? '7px' : 'initial',
                }}>
                <VStack spacing="26px">
                    <FormControl px={{ sm: '15px' }}>
                        <CanopyInput
                            id="verificationCode"
                            width={[
                                '268px',
                                '315px',
                                '315px',
                                '420px',
                                '420px',
                            ]}
                            placeholder={`${t(
                                'two-factor.enter-verification-code'
                            )}`}
                            height="45px"
                            maxLength={6}
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ) =>
                                setVerificationCode(event.target.value)
                            }></CanopyInput>
                    </FormControl>
                    {error && (
                        <ErrorBox
                            p="0"
                            m="0"
                            backgroundColor="inherit"
                            color={currentColors.errorText}
                            fontSize="14px"
                            fontWeight="700"
                            textAlign="center"
                            position="relative"
                            error={error}></ErrorBox>
                    )}
                    <CanopyButton
                        type="submit"
                        width={['268px', '315px', '315px', '420px', '420px']}
                        height="45px"
                        mt="42px"
                        isDisabled={!verificationCode}
                        isLoading={isLoading}>
                        {t('submit')}
                    </CanopyButton>
                </VStack>
            </form>
            <Button
                fontSize="sm"
                variant="link"
                width={['50%', '50%', '50%', '120px', '120px']}
                height="20px"
                mt="20px"
                color={currentColors.brand['300']}
                _hover={{ textDecoration: 'none' }}
                _focus={{ border: 'none' }}
                onClick={() => props.setLoginFlow('login')}>
                {t('forgot-password.return-to-login')}
            </Button>
        </>
    );
};
Verification.propTypes = {
    t: PropTypes.func.isRequired,
};

export default withTranslation('translations')(Verification);
